import React, { useState, useEffect, useCallback } from 'react';
import { 
  Facebook, 
  Instagram, 
  Linkedin, 
  Youtube,
  MessageCircle,
  Send,
  Calendar,
  Clock,
  Image,
  Video,
  FileText,
  Sparkles,
  Plus,
  Settings,
  BarChart3,
  Loader2,
  CheckCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  Hash,
  Building2,
  RefreshCw,
  Webhook,
  Play
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import * as api from '../services/api';

const PLATFORMS = [
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F' },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: '#FF0000' },
  { id: 'tiktok', name: 'TikTok', icon: Video, color: '#000000' },
  { id: 'whatsapp', name: 'WhatsApp', icon: MessageCircle, color: '#25D366' }
];

const CONTENT_TYPES = [
  { id: 'post', name: 'Publicación', icon: FileText },
  { id: 'story', name: 'Historia', icon: Image },
  { id: 'reel_script', name: 'Guión de Reel', icon: Video },
  { id: 'ad', name: 'Anuncio', icon: BarChart3 }
];

const TONES = [
  { id: 'profesional', name: 'Profesional' },
  { id: 'casual', name: 'Casual' },
  { id: 'divertido', name: 'Divertido' },
  { id: 'inspirador', name: 'Inspirador' },
  { id: 'educativo', name: 'Educativo' }
];

const MarketingPage = ({ currentUser, globalSearch = '' }) => {
  const [activeTab, setActiveTab] = useState('create');
  const [empresas, setEmpresas] = useState([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [posts, setPosts] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Content creation state
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [contentType, setContentType] = useState('post');
  const [postText, setPostText] = useState('');
  const [mediaUrls, setMediaUrls] = useState([]);
  const [hashtags, setHashtags] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  
  // AI generation state
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiTone, setAiTone] = useState('profesional');
  const [aiPlatform, setAiPlatform] = useState('instagram');
  const [aiContentType, setAiContentType] = useState('post');
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  
  // Social config modal
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [socialConfig, setSocialConfig] = useState({});
  
  // Webhook modal
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [webhookForm, setWebhookForm] = useState({
    name: '',
    url: '',
    events: [],
    secret: ''
  });
  
  const [publishing, setPublishing] = useState(false);
  const [publishResults, setPublishResults] = useState(null);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [empresasRes, webhooksRes] = await Promise.all([
        api.getEmpresas(),
        api.default.get('/webhooks')
      ]);
      setEmpresas(empresasRes.data || []);
      setWebhooks(webhooksRes.data || []);
      
      if (empresasRes.data?.length > 0 && !selectedEmpresa) {
        setSelectedEmpresa(empresasRes.data[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedEmpresa]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load posts when empresa changes
  useEffect(() => {
    const loadPosts = async () => {
      if (selectedEmpresa) {
        try {
          const [postsRes, scheduledRes, configRes] = await Promise.all([
            api.default.get(`/social/posts?empresa_id=${selectedEmpresa}`),
            api.default.get(`/social/posts/scheduled?empresa_id=${selectedEmpresa}`),
            api.default.get(`/empresas/${selectedEmpresa}/social-config`)
          ]);
          setPosts(postsRes.data || []);
          setScheduledPosts(scheduledRes.data || []);
          setSocialConfig(configRes.data || {});
        } catch (error) {
          console.error('Error loading posts:', error);
        }
      }
    };
    loadPosts();
  }, [selectedEmpresa]);

  // Toggle platform selection
  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId) 
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  // Generate AI content
  const handleGenerateContent = async () => {
    if (!aiTopic.trim()) return;
    
    setGenerating(true);
    try {
      const response = await api.default.post('/ai/generate-social-content', {
        empresa_id: selectedEmpresa,
        platform: aiPlatform,
        content_type: aiContentType,
        topic: aiTopic,
        tone: aiTone,
        include_hashtags: true,
        include_cta: true
      });
      
      setGeneratedContent(response.data);
    } catch (error) {
      console.error('Error generating content:', error);
      alert('Error al generar contenido');
    } finally {
      setGenerating(false);
    }
  };

  // Use generated content
  const useGeneratedContent = () => {
    if (generatedContent) {
      setPostText(generatedContent.content);
      if (generatedContent.hashtags?.length > 0) {
        setHashtags(generatedContent.hashtags.join(' '));
      }
      setShowAIModal(false);
      setGeneratedContent(null);
      setAiTopic('');
    }
  };

  // Publish post
  const handlePublish = async () => {
    if (!selectedEmpresa || selectedPlatforms.length === 0 || !postText.trim()) {
      alert('Selecciona al menos una plataforma y escribe el contenido');
      return;
    }
    
    setPublishing(true);
    try {
      const response = await api.default.post(`/social/posts?user_id=${currentUser?.id}`, {
        empresa_id: selectedEmpresa,
        platforms: selectedPlatforms,
        content_type: contentType,
        text: postText,
        media_urls: mediaUrls.filter(u => u.trim()),
        hashtags: hashtags.split(/\s+/).filter(h => h.trim()),
        link_url: linkUrl || null,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null
      });
      
      setPublishResults(response.data);
      
      // Reset form if successful
      if (response.data.results?.some(r => r.success) || response.data.scheduled) {
        setPostText('');
        setHashtags('');
        setLinkUrl('');
        setMediaUrls([]);
        setScheduledAt('');
        setSelectedPlatforms([]);
      }
    } catch (error) {
      console.error('Error publishing:', error);
      alert('Error al publicar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPublishing(false);
    }
  };

  // Save social config
  const handleSaveConfig = async () => {
    try {
      await api.default.put(`/empresas/${selectedEmpresa}/social-config`, socialConfig);
      setShowConfigModal(false);
      alert('Configuración guardada');
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Error al guardar configuración');
    }
  };

  // Create webhook
  const handleCreateWebhook = async () => {
    if (!webhookForm.name || !webhookForm.url || webhookForm.events.length === 0) {
      alert('Completa todos los campos requeridos');
      return;
    }
    
    try {
      await api.default.post('/webhooks', webhookForm);
      loadData();
      setShowWebhookModal(false);
      setWebhookForm({ name: '', url: '', events: [], secret: '' });
    } catch (error) {
      console.error('Error creating webhook:', error);
      alert('Error al crear webhook');
    }
  };

  // Test webhook
  const handleTestWebhook = async (webhookId) => {
    try {
      const response = await api.default.post(`/webhooks/${webhookId}/test`);
      alert(response.data.success ? 'Webhook enviado correctamente' : 'Error al enviar webhook');
    } catch (error) {
      console.error('Error testing webhook:', error);
      alert('Error al probar webhook');
    }
  };

  const selectedEmpresaData = empresas.find(e => e.id === selectedEmpresa);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-[#D4A843]" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="marketing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Marketing & Social Media</h1>
          <p className="text-gray-500 text-sm mt-1">
            Gestiona contenido y publicaciones para tus clientes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedEmpresa || ''} onValueChange={setSelectedEmpresa}>
            <SelectTrigger className="w-[250px]">
              <Building2 size={16} className="mr-2 text-gray-500" />
              <SelectValue placeholder="Seleccionar empresa" />
            </SelectTrigger>
            <SelectContent>
              {empresas.map(emp => (
                <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setShowConfigModal(true)}
            title="Configurar redes sociales"
          >
            <Settings size={18} />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        {[
          { id: 'create', label: 'Crear Contenido', icon: Plus },
          { id: 'scheduled', label: 'Programados', icon: Calendar },
          { id: 'history', label: 'Historial', icon: Clock },
          { id: 'webhooks', label: 'Webhooks', icon: Webhook }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id 
                ? 'bg-[#D4A843] text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Create Content Tab */}
      {activeTab === 'create' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Content Editor */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">Contenido</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAIModal(true)}
                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                >
                  <Sparkles size={16} className="mr-2" />
                  Generar con AI
                </Button>
              </div>
              
              <Textarea
                value={postText}
                onChange={e => setPostText(e.target.value)}
                placeholder="Escribe tu publicación aquí..."
                rows={6}
                className="mb-4"
                data-testid="post-content-input"
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>URL del Media (imagen/video)</Label>
                  <Input
                    value={mediaUrls[0] || ''}
                    onChange={e => setMediaUrls([e.target.value])}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label>Link (opcional)</Label>
                  <Input
                    value={linkUrl}
                    onChange={e => setLinkUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <Label>Hashtags</Label>
                <Input
                  value={hashtags}
                  onChange={e => setHashtags(e.target.value)}
                  placeholder="#marketing #socialmedia #contenido"
                />
              </div>
            </div>
            
            {/* Publish Results */}
            {publishResults && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="font-medium mb-3">Resultados de publicación:</h4>
                {publishResults.scheduled ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <Calendar size={16} />
                    Publicación programada correctamente
                  </div>
                ) : publishResults.results?.map((r, idx) => (
                  <div key={idx} className={`flex items-center gap-2 mb-2 ${r.success ? 'text-green-600' : 'text-red-600'}`}>
                    {r.success ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                    <span className="capitalize">{r.platform}:</span>
                    {r.success ? (
                      <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                        Ver publicación <ExternalLink size={12} className="inline" />
                      </a>
                    ) : (
                      <span>{r.error}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="space-y-4">
            {/* Platform Selection */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-800 mb-4">Plataformas</h3>
              <div className="grid grid-cols-2 gap-2">
                {PLATFORMS.map(platform => {
                  const isSelected = selectedPlatforms.includes(platform.id);
                  const isConfigured = socialConfig?.[`${platform.id}_page_id`] || 
                                       socialConfig?.[`${platform.id}_account_id`] ||
                                       socialConfig?.[`${platform.id}_company_id`] ||
                                       socialConfig?.[`${platform.id}_phone_id`];
                  
                  return (
                    <button
                      key={platform.id}
                      onClick={() => togglePlatform(platform.id)}
                      disabled={!isConfigured}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                        isSelected 
                          ? 'border-2 bg-opacity-10' 
                          : 'border-gray-200 hover:border-gray-300'
                      } ${!isConfigured ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={isSelected ? { 
                        borderColor: platform.color, 
                        backgroundColor: `${platform.color}10`
                      } : {}}
                      title={isConfigured ? '' : 'No configurado'}
                    >
                      <platform.icon size={20} style={{ color: platform.color }} />
                      <span className="text-sm">{platform.name}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Configura las cuentas en <button onClick={() => setShowConfigModal(true)} className="text-[#D4A843] hover:underline">ajustes</button>
              </p>
            </div>
            
            {/* Content Type */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-800 mb-4">Tipo de Contenido</h3>
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTENT_TYPES.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      <span className="flex items-center gap-2">
                        <type.icon size={14} />
                        {type.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Schedule */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-800 mb-4">Programar</h3>
              <Input
                type="datetime-local"
                value={scheduledAt}
                onChange={e => setScheduledAt(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-2">
                Deja vacío para publicar ahora
              </p>
            </div>
            
            {/* Publish Button */}
            <Button
              onClick={handlePublish}
              disabled={publishing || selectedPlatforms.length === 0 || !postText.trim()}
              className="w-full bg-[#D4A843] hover:bg-[#c49a3a] text-white"
              data-testid="publish-btn"
            >
              {publishing ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : scheduledAt ? (
                <Calendar size={16} className="mr-2" />
              ) : (
                <Send size={16} className="mr-2" />
              )}
              {scheduledAt ? 'Programar Publicación' : 'Publicar Ahora'}
            </Button>
          </div>
        </div>
      )}

      {/* Scheduled Tab */}
      {activeTab === 'scheduled' && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Publicaciones Programadas</h3>
          {scheduledPosts.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No hay publicaciones programadas</p>
          ) : (
            <div className="space-y-3">
              {scheduledPosts.map(post => (
                <div key={post.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-800 line-clamp-2">{post.text}</p>
                      <div className="flex items-center gap-2 mt-2">
                        {post.platforms?.map(p => {
                          const platform = PLATFORMS.find(pl => pl.id === p);
                          return platform ? (
                            <platform.icon key={p} size={16} style={{ color: platform.color }} />
                          ) : null;
                        })}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        <Calendar size={12} className="inline mr-1" />
                        {new Date(post.scheduled_at).toLocaleString('es-ES')}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => api.default.post(`/social/posts/${post.id}/publish`)}
                      >
                        <Play size={12} className="mr-1" />
                        Publicar Ahora
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Historial de Publicaciones</h3>
          {posts.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No hay publicaciones</p>
          ) : (
            <div className="space-y-3">
              {posts.filter(p => p.status !== 'scheduled').map(post => (
                <div key={post.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-gray-800 line-clamp-2">{post.text}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center gap-1">
                          {post.platforms?.map(p => {
                            const platform = PLATFORMS.find(pl => pl.id === p);
                            return platform ? (
                              <platform.icon key={p} size={14} style={{ color: platform.color }} />
                            ) : null;
                          })}
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          post.status === 'published' ? 'bg-green-100 text-green-700' :
                          post.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {post.status === 'published' ? 'Publicado' :
                           post.status === 'failed' ? 'Fallido' : post.status}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      {new Date(post.created_at).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Webhooks Tab */}
      {activeTab === 'webhooks' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-800">Webhooks para n8n y Automatizaciones</h3>
            <Button onClick={() => setShowWebhookModal(true)} className="bg-[#D4A843] hover:bg-[#c49a3a]">
              <Plus size={16} className="mr-2" />
              Nuevo Webhook
            </Button>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200">
            {webhooks.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No hay webhooks configurados</p>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Nombre</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">URL</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Eventos</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Stats</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {webhooks.map(webhook => (
                    <tr key={webhook.id} className="border-t border-gray-100">
                      <td className="px-4 py-3">
                        <span className="font-medium">{webhook.name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {webhook.url.substring(0, 40)}...
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {webhook.events?.length || 0} eventos
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500">
                          {webhook.trigger_count || 0} enviados
                          {webhook.failure_count > 0 && (
                            <span className="text-red-500 ml-1">
                              ({webhook.failure_count} fallidos)
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleTestWebhook(webhook.id)}
                        >
                          <RefreshCw size={14} className="mr-1" />
                          Test
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* AI Generation Modal */}
      <Dialog open={showAIModal} onOpenChange={setShowAIModal}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="text-purple-500" size={20} />
              Generar Contenido con AI
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            <div>
              <Label>Tema o descripción</Label>
              <Textarea
                value={aiTopic}
                onChange={e => setAiTopic(e.target.value)}
                placeholder="Ej: Promoción de nuestros servicios de video corporativo para el sector salud"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plataforma</Label>
                <Select value={aiPlatform} onValueChange={setAiPlatform}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.filter(p => p.id !== 'whatsapp').map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tono</Label>
                <Select value={aiTone} onValueChange={setAiTone}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TONES.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Tipo de contenido</Label>
              <Select value={aiContentType} onValueChange={setAiContentType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTENT_TYPES.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {generatedContent && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <Label className="text-purple-700 mb-2 block">Contenido Generado:</Label>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{generatedContent.content}</p>
                <div className="flex items-center gap-2 mt-3 text-xs text-purple-600">
                  <Hash size={12} />
                  {generatedContent.hashtags?.length || 0} hashtags
                  <span className="mx-2">•</span>
                  {generatedContent.character_count} caracteres
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="border-t border-gray-200 pt-4">
            <Button variant="outline" onClick={() => setShowAIModal(false)}>
              Cancelar
            </Button>
            {generatedContent ? (
              <Button onClick={useGeneratedContent} className="bg-purple-600 hover:bg-purple-700">
                Usar este contenido
              </Button>
            ) : (
              <Button 
                onClick={handleGenerateContent} 
                disabled={generating || !aiTopic.trim()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {generating ? <Loader2 className="animate-spin mr-2" size={16} /> : <Sparkles size={16} className="mr-2" />}
                Generar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Social Config Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="text-[#D4A843]" size={20} />
              Configuración de Redes - {selectedEmpresaData?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            <p className="text-sm text-gray-500">
              Configura los IDs de las páginas/cuentas de este cliente:
            </p>
            
            {PLATFORMS.map(platform => (
              <div key={platform.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <platform.icon size={18} style={{ color: platform.color }} />
                  <span className="font-medium">{platform.name}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">
                      {platform.id === 'whatsapp' ? 'Phone ID' : 
                       platform.id === 'linkedin' ? 'Company ID' : 'Page/Account ID'}
                    </Label>
                    <Input
                      value={socialConfig[`${platform.id}_${platform.id === 'linkedin' ? 'company' : platform.id === 'whatsapp' ? 'phone' : 'page'}_id`] || ''}
                      onChange={e => setSocialConfig(prev => ({
                        ...prev,
                        [`${platform.id}_${platform.id === 'linkedin' ? 'company' : platform.id === 'whatsapp' ? 'phone' : 'page'}_id`]: e.target.value
                      }))}
                      placeholder={`ID de ${platform.name}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Nombre/Username</Label>
                    <Input
                      value={socialConfig[`${platform.id}_${platform.id === 'linkedin' ? 'company' : platform.id === 'instagram' || platform.id === 'tiktok' ? 'username' : 'page'}_name`] || ''}
                      onChange={e => setSocialConfig(prev => ({
                        ...prev,
                        [`${platform.id}_${platform.id === 'linkedin' ? 'company' : platform.id === 'instagram' || platform.id === 'tiktok' ? 'username' : 'page'}_name`]: e.target.value
                      }))}
                      placeholder="Nombre visible"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="border-t border-gray-200 pt-4">
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConfig} className="bg-[#D4A843] hover:bg-[#c49a3a]">
              Guardar Configuración
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Webhook Modal */}
      <Dialog open={showWebhookModal} onOpenChange={setShowWebhookModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Webhook className="text-[#D4A843]" size={20} />
              Nuevo Webhook
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre</Label>
              <Input
                value={webhookForm.name}
                onChange={e => setWebhookForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: n8n Lead Notifications"
              />
            </div>
            <div>
              <Label>URL del Webhook</Label>
              <Input
                value={webhookForm.url}
                onChange={e => setWebhookForm(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://n8n.tudominio.com/webhook/..."
              />
            </div>
            <div>
              <Label>Secret (opcional)</Label>
              <Input
                value={webhookForm.secret}
                onChange={e => setWebhookForm(prev => ({ ...prev, secret: e.target.value }))}
                placeholder="Para verificar firma HMAC"
              />
            </div>
            <div>
              <Label>Eventos</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 max-h-[200px] overflow-y-auto">
                {[
                  { value: 'form.submitted', label: 'Formulario enviado' },
                  { value: 'empresa.created', label: 'Empresa creada' },
                  { value: 'empresa.updated', label: 'Empresa actualizada' },
                  { value: 'task.created', label: 'Tarea creada' },
                  { value: 'task.completed', label: 'Tarea completada' },
                  { value: 'deal.won', label: 'Deal ganado' },
                  { value: 'deal.lost', label: 'Deal perdido' },
                  { value: 'contact.added', label: 'Contacto añadido' }
                ].map(event => (
                  <label key={event.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={webhookForm.events.includes(event.value)}
                      onChange={e => {
                        if (e.target.checked) {
                          setWebhookForm(prev => ({ ...prev, events: [...prev.events, event.value] }));
                        } else {
                          setWebhookForm(prev => ({ ...prev, events: prev.events.filter(ev => ev !== event.value) }));
                        }
                      }}
                      className="rounded"
                    />
                    {event.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWebhookModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateWebhook} className="bg-[#D4A843] hover:bg-[#c49a3a]">
              Crear Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketingPage;
